import { createAjv, fixSchema, isInvalidPropertyRequired, parseDataPath } from '@takeshape/json-schema';
import { EditorState } from 'draft-js';
// biome-ignore lint/suspicious/noShadowRestrictedNames: Immutable.js Map/Set
import { fromJS, List, Map, Range, Set } from 'immutable';
import memoize from 'lodash/memoize';
import toPath from 'lodash/toPath';
import { setIn, updateIn } from '../common/utils/immutable-util.js';
import {
  ADD_ARRAY_ITEM,
  BLUR_FIELD,
  CHANGE_DISCRIMINATOR_FIELD,
  CHANGE_FIELD,
  CLEAR_ARRAY,
  CLEAR_FORM,
  CLEAR_PLACEHOLDER,
  COLLAPSE_ALL_ARRAY_ITEMS,
  CUSTOM_VALIDATION,
  EXPAND_ALL_ARRAY_ITEMS,
  FOCUS_FIELD,
  INIT_ARRAY,
  INSERT_PLACEHOLDER,
  REGISTER_FORM,
  REMOVE_ARRAY_ITEM,
  RESET_FORM,
  SET_BUSY,
  SET_FORM_DATA,
  SET_UI,
  SUBMIT_FORM,
  SUBMIT_FORM_CANCELLED,
  SUBMIT_FORM_SUCCESS,
  SWAP_ARRAY_ITEMS,
  TOGGLE_ARRAY_ITEM,
  VALIDATE_FORM
} from './action-types';
import {
  getBusyPath,
  getCollapsedPath,
  getDataPath,
  getFormPath,
  getInitialDataPath,
  getInitialDirtyPath,
  getKeysPath,
  getPlaceholderPath,
  getSchemaPath,
  getStructuralChangesPath,
  getUiPath,
  getUiPropertiesPath,
  PROPERTIES,
  pathToString,
  reducerFactory
} from './paths';
import { mergeUnionData } from './util/discriminator.js';
import isDirty from './util/is-dirty.js';
import { createResolveRef as baseCreateResolveRef } from './util/refs.js';
import { deserializeData, toPojo } from './util/serializer.js';
import { initStructuralChanges } from './util/structural-changes.js';

const ajv = createAjv({
  transpile: false
});

function ignoreEditorState(obj) {
  return obj instanceof EditorState ? {} : obj;
}

const emptyMap = Map();
const emptySet = Set();
const schemaToJS = memoize((iSchema) => fixSchema(iSchema?.toJS ? iSchema.toJS() : iSchema));
const compile = memoize((schema) => ajv.compile(schemaToJS(schema)));

const initialState = Map({
  forms: Map()
});

function resetFormHelper(state, formName, overrides) {
  return state.mergeIn(getFormPath(formName, {}), {
    previous: emptyMap,
    errors: emptyMap,
    submitErrors: emptyMap,
    touched: emptySet,
    submitted: false,
    ...overrides
  });
}

function getInitialData(schema, initialData) {
  const data = deserializeData(schema, initialData ?? {}, true);
  const ajv = createAjv({ useDefaults: true });
  // Utilize the side-effect of defaults being set
  ajv.validate(schemaToJS(schema), data);
  return fromJS(data);
}

function registerForm(state, { meta, payload }) {
  const { autoSubmit = false, validateOnBlur = false } = payload;
  const schema = fromJS(payload.schema);
  const initialData = getInitialData(schema, payload.initialData);
  const initialDirty = payload.initialDirty ? payload.initialDirty : emptySet;

  const data = initialData;
  const dirty = initialDirty;
  const busy = emptySet;

  return resetFormHelper(state, meta.formName, {
    schema,
    data,
    dirty,
    initialData,
    initialDirty,
    structuralChanges: initStructuralChanges(schema, initialData),
    busy,
    autoSubmit,
    validateOnBlur
  });
}

function clearForm(state, { meta }) {
  return state.withMutations((mutable) => {
    mutable.removeIn(getFormPath(meta.formName));
    mutable.removeIn(getUiPath(meta));
  });
}

function resetForm(state, { meta, payload }) {
  let data;
  if (payload.initialData) {
    data = fromJS(payload.initialData);
  } else {
    data = state.getIn(getInitialDataPath(meta));
  }

  const dirty = state.getIn(getInitialDirtyPath(meta));
  const result = resetFormHelper(state, meta.formName, {
    data,
    dirty
  });
  return result;
}

function initArray(state, { meta }) {
  const uiPath = getUiPropertiesPath(meta);
  const ui = state.getIn(uiPath);
  if (!meta.reinit && ui?.get('arrayKeys') && ui.get('collapsed')) {
    return state;
  }

  const array = state.getIn(getDataPath(meta));
  const indexes = array ? Range(0, array.size).toList() : List();
  return state.mergeIn(uiPath, {
    arrayKeys: indexes,
    collapsed: meta.collapseByDefault ? new Set(indexes) : new Set()
  });
}

function clearArray(state, { meta }) {
  return state.removeIn(getUiPath(meta));
}

function getNextKey(keys) {
  return keys.size === 0 ? 0 : keys.max() + 1;
}

function updateStructuralChangeProps(updater) {
  return (changes) => changes?.update(PROPERTIES, (props) => props && updater(props));
}

function addKeyStructuralChange(meta) {
  const { index } = meta;
  return updateStructuralChangeProps((props) => {
    const arrayKeys = props.get('arrayKeys');
    const nextKey = props.get('nextKey');
    return props.merge({
      arrayKeys: index === undefined ? arrayKeys.push(nextKey) : arrayKeys.insert(index, nextKey),
      nextKey: nextKey + 1
    });
  });
}

function removeKeyStructuralChange(meta) {
  const { index } = meta;
  return updateStructuralChangeProps((props) => {
    const arrayKeys = props.get('arrayKeys');
    return props.merge({
      arrayKeys: arrayKeys.remove(index)
    });
  });
}

function insertItem(list, item, index) {
  return index === undefined ? list.push(item) : list.insert(index, item);
}

function addArrayItems(state, action) {
  const { meta, payload } = action;
  const { index } = meta;
  const item = fromJS(payload);
  return state.withMutations((mutableState) => {
    mutableState.updateIn(getStructuralChangesPath(meta), addKeyStructuralChange(meta));
    updateIn(mutableState, getDataPath(meta), (list) => (list ? insertItem(list, item, index) : List.of(item)));
    updateIn(mutableState, getKeysPath(meta), (keys) =>
      keys ? insertItem(keys, getNextKey(keys), index) : List.of(0)
    );

    clearPlaceholder(mutableState, action);
  });
}

function swapIn(mutableState, path, indexA, indexB) {
  const pathA = path.concat([indexA]);
  const pathB = path.concat([indexB]);
  const a = mutableState.getIn(pathA);
  const b = mutableState.getIn(pathB);

  if (a !== undefined && b !== undefined) {
    mutableState.setIn(pathA, b).setIn(pathB, a);
  }
}

function swap(state, indexA, indexB) {
  const a = state.get(indexA);
  const b = state.get(indexB);
  return state.set(indexA, b).set(indexB, a);
}

function copyOrDelete(state, from, to) {
  const value = state.get(from);
  return value ? state.set(to, value) : state.delete(from);
}

const arrayKeysPath = [PROPERTIES, 'arrayKeys'];

function swapChange(state, indexA, indexB) {
  return state.withMutations((mutableState) => {
    const pathA = String(indexA);
    const pathB = String(indexB);
    copyOrDelete(mutableState, pathA, pathB);
    copyOrDelete(mutableState, pathB, pathA);
    mutableState.updateIn(arrayKeysPath, (keys) => swap(keys, indexA, indexB));
  });
}

function swapArrayItems(state, action) {
  return state.withMutations((mutableState) => {
    const { meta } = action;
    setTouched(mutableState, action);

    mutableState.updateIn(getStructuralChangesPath(meta), (changes) => {
      return changes && swapChange(changes, meta.indexA, meta.indexB);
    });

    swapIn(mutableState, getDataPath(meta), meta.indexA, meta.indexB);
    updateIn(mutableState, getUiPath(meta), (ui) => ui && swapChange(ui, meta.indexA, meta.indexB));
  });
}

function removeArrayItem(state, { meta }) {
  return state.withMutations((mutableState) => {
    mutableState.updateIn(getStructuralChangesPath(meta), removeKeyStructuralChange(meta));
    updateIn(mutableState, getKeysPath(meta), (keys) => (keys ? keys.remove(meta.index) : keys));
    updateIn(mutableState, getDataPath(meta), (keys) => (keys ? keys.remove(meta.index) : keys));
  });
}

function insertPlaceholder(state, { meta, payload }) {
  return state.setIn(getPlaceholderPath(meta), Map({ index: meta.index, value: payload }));
}

function clearPlaceholder(state, { meta }) {
  return state.removeIn(getPlaceholderPath(meta));
}

function toggleArrayItem(state, { meta, payload }) {
  return state.updateIn(getCollapsedPath(meta), (collapsed) =>
    collapsed.has(payload) ? collapsed.remove(payload) : collapsed.add(payload)
  );
}

function expandAllArrayItems(state, { meta }) {
  return state.setIn(getCollapsedPath(meta), emptySet);
}

function getCollapseAllUpdate(obj, update = {}) {
  if (Map.isMap(obj)) {
    obj.forEach((value, key) => {
      if (key === PROPERTIES) {
        const arrayKeys = value.get('arrayKeys');
        if (arrayKeys) {
          update[PROPERTIES] = { collapsed: arrayKeys.toSet() };
        }
      } else {
        update[key] = {};
        getCollapseAllUpdate(value, update[key]);
      }
    });
  }

  return update;
}

function collapseAllArrayItems(state, { meta }) {
  const uiPath = getUiPath(meta);
  const update = getCollapseAllUpdate(state.getIn(uiPath));
  return state.mergeDeepIn(uiPath, update);
}

function changeField(state, { meta, payload }) {
  return setIn(state, getDataPath(meta), fromJS(payload === '' ? undefined : payload));
}

const createResolveRef = memoize((rootSchema) => baseCreateResolveRef(rootSchema));
function getRefResolver(state, meta) {
  return createResolveRef(state.getIn(getSchemaPath(meta)));
}

function changeDiscriminatorField(state, { meta, payload }) {
  const parentPath = getDataPath(meta);
  const discriminatorName = parentPath.pop();

  const currentObj = state.getIn(parentPath);
  if (!currentObj || currentObj.get(discriminatorName) !== payload) {
    const unionSchema = state.getIn(getSchemaPath(meta).concat(meta.schemaPath));

    const newObj = mergeUnionData(
      getRefResolver(state, meta),
      unionSchema,
      { [discriminatorName]: payload },
      currentObj
    );
    return setIn(state, parentPath, fromJS(newObj));
  }

  return setIn(state, getDataPath(meta), fromJS(payload === '' ? undefined : payload));
}

function setUI(state, { meta, payload }) {
  return state.mergeIn(getUiPropertiesPath(meta), fromJS(payload));
}

function setBusy(state, { meta, payload }) {
  const name = meta.path.join('.');
  const path = getBusyPath(meta);
  const currentValue = state.getIn(path) ?? emptySet;
  const newValue = payload ? currentValue.add(name) : currentValue.delete(name);
  return setIn(state, path, newValue);
}

function setDirty(dirty, path, isDirty) {
  const pathStr = pathToString(path);
  return isDirty ? dirty.add(pathStr) : dirty.remove(pathStr);
}

function checkIsDirty(state, meta) {
  const initialData = state.getIn(getInitialDataPath(meta));
  const currentData = state.getIn(getDataPath(meta));

  return isDirty(initialData, currentData);
}

function focusField(state, { meta }) {
  const fieldPath = pathToString(meta.path);
  return state.removeIn(['forms', meta.formName, 'errors', fieldPath]);
}

// IN /properties/UxGVhhj4t/@relationship OUT properties.UxGVhhj4t.@relationship
export function normalizeDataPath(instancePath) {
  return parseDataPath(instancePath).join('.');
}

function setTouched(state, { meta }) {
  return state.updateIn(['forms', meta.formName, 'touched'], (touched) =>
    (touched || emptySet).add(pathToString(meta.path))
  );
}

export function getIsNullObject(error, data, path) {
  return error.keyword === 'type' && error.params.type === 'object' && data.getIn(path.split('.')) === null;
}

function validate(state, { meta }) {
  const autoSubmit = state.getIn(['forms', meta.formName, 'autoSubmit']);
  const submitted = state.getIn(['forms', meta.formName, 'submitted']);
  const touched = state.getIn(['forms', meta.formName, 'touched'], emptySet);
  const data = state.getIn(['forms', meta.formName, 'data']);

  const errors = {};
  const hiddenErrors = {};
  const schema = state.getIn(getSchemaPath(meta));
  if (schema) {
    const validate = compile(schema);
    const valid = validate(toPojo(data, ignoreEditorState));
    if (!valid) {
      const pojoSchema = schemaToJS(schema);
      for (const error of validate.errors) {
        let path;
        let isRequiredError = Boolean(error.params?.missingProperty);

        if (isRequiredError) {
          path = (error.instancePath ? `${normalizeDataPath(error.instancePath)}.` : '') + error.params.missingProperty;
        } else {
          path = normalizeDataPath(error.instancePath);
        }

        const isNullObject = getIsNullObject(error, data, path);
        let ignoreError = isNullObject;
        if (isNullObject) {
          if (isInvalidPropertyRequired(pojoSchema, error)) {
            isRequiredError = true;
            ignoreError = false;
          }
        }

        if (!ignoreError) {
          const errorString = isRequiredError ? 'This field is required.' : error.message;
          if (path && (submitted || touched.has(path) || autoSubmit)) {
            errors[path] = errorString;
          } else {
            hiddenErrors[path] = errorString;
          }
        }
      }
    }
  }

  // Check to see if the last touched field is dirty
  // If the last touched field is orphaned because an ancestor was set to undefined, set
  // the closest surviving ancestor to dirty

  let dirty = state.getIn(['forms', meta.formName, 'dirty'], emptySet);

  if (data) {
    const checkIsPathDirty = (path) => {
      while (path.length > 0) {
        const parentPath = path.slice(0, path.length - 1);
        const parentExists = parentPath.length === 0 || data.getIn(parentPath) !== undefined;
        if (parentExists) {
          dirty = setDirty(dirty, path, checkIsDirty(state, { formName: meta.formName, path }));
          break;
        }
        path = parentPath;
      }
    };

    if (meta.path) {
      checkIsPathDirty(meta.path);
    }

    for (const pathStr of touched) {
      checkIsPathDirty(toPath(pathStr));
    }
  }

  return state.mergeIn(['forms', meta.formName], {
    errors: Map(errors),
    hiddenErrors: Map(hiddenErrors),
    dirty
  });
}

function customValidation(state, { payload, meta }) {
  return state.mergeIn(['forms', meta.formName, 'errors'], Map(payload));
}

function submitForm(state, { meta }) {
  return state
    .mergeIn(['forms', meta.formName, 'previous'], {
      submitErrors: state.getIn(['forms', meta.formName, 'submitErrors']),
      submitted: state.getIn(['forms', meta.formName, 'submitted'])
    })
    .mergeIn(['forms', meta.formName], {
      submitErrors: emptyMap,
      submitted: true
    });
}

function submitFormSuccess(state, { meta }) {
  return state
    .mergeIn(['forms', meta.formName, 'previous'], {
      dirty: state.getIn(['forms', meta.formName, 'dirty']),
      touched: state.getIn(['forms', meta.formName, 'touched']),
      structuralChanges: state.getIn(['forms', meta.formName, 'structuralChanges'])
    })
    .mergeIn(['forms', meta.formName], {
      dirty: emptySet,
      touched: emptySet,
      structuralChanges: emptyMap
    });
}

function submitFormCancelled(state, { meta }) {
  return state
    .mergeIn(['forms', meta.formName], state.getIn(['forms', meta.formName, 'previous']))
    .setIn(['forms', meta.formName, 'previous'], emptyMap);
}

function setFormData(state, { meta, payload }) {
  const path = ['forms', meta.formName, 'data'].concat(meta.path);

  if (payload === undefined) {
    return state.removeIn(path);
  }

  return state.setIn(path, payload);
}

export default reducerFactory(initialState, {
  [REGISTER_FORM]: registerForm,
  [CLEAR_FORM]: clearForm,
  [RESET_FORM]: resetForm,
  [INIT_ARRAY]: initArray,
  [CLEAR_ARRAY]: clearArray,
  [ADD_ARRAY_ITEM]: addArrayItems,
  [SWAP_ARRAY_ITEMS]: swapArrayItems,
  [REMOVE_ARRAY_ITEM]: removeArrayItem,
  [INSERT_PLACEHOLDER]: insertPlaceholder,
  [CLEAR_PLACEHOLDER]: clearPlaceholder,
  [TOGGLE_ARRAY_ITEM]: toggleArrayItem,
  [EXPAND_ALL_ARRAY_ITEMS]: expandAllArrayItems,
  [COLLAPSE_ALL_ARRAY_ITEMS]: collapseAllArrayItems,
  [CHANGE_FIELD]: changeField,
  [CHANGE_DISCRIMINATOR_FIELD]: changeDiscriminatorField,
  [SET_FORM_DATA]: setFormData,
  [FOCUS_FIELD]: focusField,
  [BLUR_FIELD]: setTouched,
  [SET_UI]: setUI,
  [SET_BUSY]: setBusy,
  [SUBMIT_FORM]: submitForm,
  [SUBMIT_FORM_SUCCESS]: submitFormSuccess,
  [SUBMIT_FORM_CANCELLED]: submitFormCancelled,
  [VALIDATE_FORM]: validate,
  [CUSTOM_VALIDATION]: customValidation
});
